'use client'

import React, { FC, useMemo, useState } from 'react'

import { FirebaseValues } from '~types/index'

import useClarityIdentify from '@/hooks/useClarityIdentify'
import { useDeadpoolAuth } from './hooks/useDeadpoolAuth'
import { useFirebaseAuth } from './hooks/useFirebaseAuth'
import { useLoggedState } from './hooks/useLoggedState'
import { AuthContextType, EnhancedAuthContextProviderProps } from './types'

export const AuthContext = React.createContext<AuthContextType>({
  user: null,
  customer: null,
  isInitialized: false,
  federatedClient: false,
})

export const useAuthContext = () => React.useContext(AuthContext)

export const AuthContextProvider: FC<EnhancedAuthContextProviderProps> = ({
  children,
  clientId,
  fbConfig,
  federatedClient,
}) => {
  const [firebaseValues, setFirebaseValues] = useState<FirebaseValues | null>(
    null
  )

  // Use custom hooks for separation of concerns
  const { user } = useFirebaseAuth({
    fbConfig,
    federatedClient,
  })
  const { customer, isInitialized } = useDeadpoolAuth({
    clientId,
  })

  // Manage logged state
  useLoggedState({ customer, isInitialized })

  // Manage Clarity user identification
  useClarityIdentify(customer)

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      user,
      customer,
      isInitialized,
      federatedClient,
      firebaseValues,
      setFirebaseValues,
    }),
    [user, customer, isInitialized, federatedClient, firebaseValues]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
